/* eslint-disable jira/filename-case */
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { ThemeColorModes } from '@atlaskit/tokens';
import { JiraWorkspaceAri } from '@atlassian/ari/jira';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	getWorkspaceThemePreference,
	updateWorkspaceThemePreference,
} from '@atlassian/shared-user-preferences-api/workspace-theme-management';
import { DEFAULT_COLOR_MODE, themeColorModes } from './themeColorModes.constants.tsx';

const handleThemePreferenceError = (
	error: unknown,
	operation: 'get' | 'put',
	additionalAttributes?: Record<string, unknown>,
) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const err = error as Error & { name?: string; statusCode?: number };
	fireErrorAnalytics({
		meta: {
			id: 'ThemePreferenceCRUD',
			packageName: 'jiraRouterResourcesThemePreference',
		},
		error: err,
		attributes: {
			op: operation,
			errName: String(err.name),
			statusCode: err.statusCode,
			...additionalAttributes,
		},
	});
};

export const setThemePreference = async (
	colorMode: ThemeColorModes,
	cloudId: string,
	activationId: string,
	accountId: string | null,
): Promise<void> => {
	try {
		// Create workspace ARI only when feature flag is enabled
		const workspaceAri = JiraWorkspaceAri.create({
			siteId: cloudId,
			activationId,
		}).toString();

		if (!accountId) {
			// Shared theming is supported for logged in users only
			return;
		}

		// Use platform package for GraphQL operations
		await updateWorkspaceThemePreference(getRelayEnvironment(), workspaceAri, accountId, {
			colorMode,
		});
	} catch (error: unknown) {
		handleThemePreferenceError(error, 'put', { param: `to:${colorMode}` });
		throw error;
	}
};
/**
 * Asynchronously retrieves the user's preferred theme from GraphQL and ensures its validity
 * against a set of known themes. Returns the valid theme or undefined for unrecognized themes.
 */
export const getThemePreference = async (
	cloudId?: string,
	activationId?: string,
	accountId?: string | null,
): Promise<ThemeColorModes> => {
	if (!cloudId || !activationId || !accountId) {
		// Shared theming is supported for logged in users only. CloudId and ActicationId should be always present but are left nullable to simplify the caller function
		return DEFAULT_COLOR_MODE;
	}
	try {
		let colorMode: ThemeColorModes | undefined;

		// Create workspace ARI only when feature flag is enabled
		const workspaceAri = JiraWorkspaceAri.create({
			siteId: cloudId,
			activationId,
		}).toString();

		// Use platform package for GraphQL operations
		const themeState = await getWorkspaceThemePreference(
			getRelayEnvironment(),
			workspaceAri,
			accountId,
		);

		colorMode = themeState?.colorMode;

		// Validate theme value
		if (colorMode && !themeColorModes.includes(colorMode)) {
			// very unlikely but…
			handleThemePreferenceError(new Error('Unrecognized theme!'), 'get', {
				theme: colorMode,
				errName: 'unrecognized value!',
			});
			colorMode = DEFAULT_COLOR_MODE;
		}

		return colorMode ?? DEFAULT_COLOR_MODE;
	} catch (error: unknown) {
		handleThemePreferenceError(error, 'get');
		throw error;
	}
};
